/**
 * Narrow ?perf=1 (+ optional testSpeed) browser bridge for phone-perf smokes.
 * Installs commit snapshot + expressive graph assertion helpers; absent otherwise.
 */
import {
  deserializeEditGraph,
  resolveEdit,
  validateEditGraphAgainstPack,
} from "@desk/core";
import { SHIFT_PACK_ORDER, PACK_REGISTRY } from "../content/registry.js";
import { assertExpressivePerfGraph } from "./assert-expressive-graph.js";
import {
  beginPerfCommitSampling,
  getPerfCommitSnapshot,
  isPerfCommitSamplingActive,
  isPerfCommitSamplingEnabled,
} from "./perf-frame-sampler.js";
import {
  beginPerfCaptureLifecycle,
  getPerfCaptureLifecycleSnapshot,
} from "./perf-capture-lifecycle.js";
import {
  registerPerfInvalidate,
  startPerfInvalidatePump,
  stopPerfInvalidatePump,
  getPerfInvalidateDepth,
} from "./perf-invalidate-pump.js";
import {
  assertCommittedPlaybackAdvance,
  assertEffectCrossings,
  type EffectCrossingRanges,
} from "./assert-committed-playback.js";
import { getArtifactCaptureQueueStats } from "../shell/lib/artifact-capture.js";

export type DeskPerfBridge = {
  beginSampling: () => void;
  getCommitSnapshot: typeof getPerfCommitSnapshot;
  getCaptureLifecycle: typeof getPerfCaptureLifecycleSnapshot;
  getQueueStats: typeof getArtifactCaptureQueueStats;
  isSamplingActive: () => boolean;
  getInvalidateDepth: () => number;
  beginCaptureLifecycle: () => void;
  assertPlaybackAdvance: (opts?: {
    minDistinctEditTicks?: number;
    minEditSpan?: number;
    samples?: ReturnType<typeof getPerfCommitSnapshot> extends infer S
      ? S extends { samples: infer T }
        ? T
        : never
      : never;
  }) => ReturnType<typeof assertCommittedPlaybackAdvance>;
  assertEffectCrossings: (
    ranges: EffectCrossingRanges & {
      samples?: ReturnType<typeof getPerfCommitSnapshot> extends infer S
        ? S extends { samples: infer T }
          ? T
          : never
        : never;
    },
  ) => ReturnType<typeof assertEffectCrossings>;
  assertExpressiveGraph: (packId?: string) => ReturnType<typeof assertExpressivePerfGraph> & {
    graphFingerprint: string;
    opSummary: Array<{ kind: string; id?: string }>;
    graphJson: string;
  };
};

function simpleFingerprint(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `fnv1a_${(h >>> 0).toString(16)}`;
}

function readPerfEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return isPerfCommitSamplingEnabled(window.location.search);
}

export function installPerfBridge(): void {
  if (typeof window === "undefined") return;
  if (!readPerfEnabled()) {
    delete window.__DESK_PERF__;
    stopPerfInvalidatePump();
    registerPerfInvalidate(null);
    return;
  }
  // Install bridge only — do not start the invalidate pump until beginSampling()
  // so normal editor / desktop PerfPanel autorun are not flooded with store nudges.
  beginPerfCaptureLifecycle(window.location.search);

  const bridge: DeskPerfBridge = {
    beginSampling: () => {
      beginPerfCommitSampling(window.location.search);
      beginPerfCaptureLifecycle(window.location.search);
      startPerfInvalidatePump();
    },
    beginCaptureLifecycle: () => {
      beginPerfCaptureLifecycle(window.location.search);
    },
    getCommitSnapshot: getPerfCommitSnapshot,
    getCaptureLifecycle: getPerfCaptureLifecycleSnapshot,
    getQueueStats: getArtifactCaptureQueueStats,
    isSamplingActive: () => isPerfCommitSamplingActive(),
    getInvalidateDepth: () => getPerfInvalidateDepth(),
    assertPlaybackAdvance: (opts) => {
      const samples = opts?.samples ?? getPerfCommitSnapshot()?.samples ?? [];
      return assertCommittedPlaybackAdvance(samples, {
        minDistinctEditTicks: opts?.minDistinctEditTicks ?? 40,
        minEditSpan: opts?.minEditSpan ?? 80,
      });
    },
    assertEffectCrossings: (ranges) => {
      const { samples: overrideSamples, ...crossingRanges } = ranges;
      const samples = overrideSamples ?? getPerfCommitSnapshot()?.samples ?? [];
      return assertEffectCrossings(samples, crossingRanges);
    },
    assertExpressiveGraph: (packId) => {
      const json =
        window.__DESK_EDITOR_GRAPH_JSON__ ||
        document.documentElement.dataset.editGraphJson ||
        "";
      if (!json) {
        return {
          ok: false,
          failures: ["missing __DESK_EDITOR_GRAPH_JSON__ / dataset.editGraphJson"],
          selected: {
            headlineText: null,
            musicAssetId: null,
            sfxAAssetId: null,
            sfxBAssetId: null,
            lookId: null,
            lookIntensityMilli: null,
            motionBeatId: null,
            motionPresetId: null,
            motionAmplitudeMilli: null,
            motionZoomDeltaMilli: null,
            stickerSlot: null,
            stickerId: null,
            transitionBeatId: null,
            transitionPresetId: null,
            transitionKind: null,
          },
          activeRanges: {
            music: false,
            sfxAAtTick: null,
            sfxBAtTick: null,
            stickerStartTick: null,
            stickerEndTick: null,
            transitionBoundaryEditHint: null,
            motionBeatStartTick: null,
            motionBeatEndTick: null,
            transitionBoundaryEditTick: null,
          },
          graphFingerprint: "",
          opSummary: [],
          graphJson: "",
        };
      }
      const id = (packId ||
        new URLSearchParams(window.location.search).get("scenario") ||
        SHIFT_PACK_ORDER[0]!) as keyof typeof PACK_REGISTRY;
      const pack = PACK_REGISTRY[id]?.pack;
      if (!pack) {
        return {
          ok: false,
          failures: [`unknown pack ${String(id)}`],
          selected: {
            headlineText: null,
            musicAssetId: null,
            sfxAAssetId: null,
            sfxBAssetId: null,
            lookId: null,
            lookIntensityMilli: null,
            motionBeatId: null,
            motionPresetId: null,
            motionAmplitudeMilli: null,
            motionZoomDeltaMilli: null,
            stickerSlot: null,
            stickerId: null,
            transitionBeatId: null,
            transitionPresetId: null,
            transitionKind: null,
          },
          activeRanges: {
            music: false,
            sfxAAtTick: null,
            sfxBAtTick: null,
            stickerStartTick: null,
            stickerEndTick: null,
            transitionBoundaryEditHint: null,
            motionBeatStartTick: null,
            motionBeatEndTick: null,
            transitionBoundaryEditTick: null,
          },
          graphFingerprint: simpleFingerprint(json),
          opSummary: [],
          graphJson: json,
        };
      }
      const graph = deserializeEditGraph(json);
      validateEditGraphAgainstPack(graph, pack);
      const resolved = resolveEdit(graph, pack);
      const assertion = assertExpressivePerfGraph(resolved, pack);
      const opSummary = graph.ops.slice(0, graph.cursor).map((op) => {
        const base: { kind: string; id?: string } = { kind: op.kind };
        if ("treatmentId" in op && op.treatmentId) base.id = String(op.treatmentId);
        if ("presetId" in op && op.presetId) base.id = String(op.presetId);
        if ("stickerId" in op && op.stickerId) base.id = String(op.stickerId);
        if ("assetId" in op && op.assetId) base.id = String(op.assetId);
        return base;
      });
      return {
        ...assertion,
        graphFingerprint: simpleFingerprint(json),
        opSummary,
        graphJson: json,
      };
    },
  };

  window.__DESK_PERF__ = bridge;
}

declare global {
  interface Window {
    __DESK_PERF__?: DeskPerfBridge;
    __DESK_EDITOR_GRAPH_JSON__?: string;
  }
}
